// frontend/src/pages/PayrollItems/index.tsx
// Thin orchestrator: URL-driven initial expand state, section toggle, and layout.
// Delegates all heavy JSX to AccordionRow + 3 section bodies + NewItemMenu.

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  X,
  Sparkles,
  CircleDollarSign,
  Receipt,
  CalendarClock,
  MapPin,
  MessageSquareText,
  Check,
} from "lucide-react";

import NovaPill from "../../components/NovaPill";

import type { SectionKey, ExpandedState } from "./types";
import { PAY_TYPES, SCHEDULES, LOCATIONS, DEDUCTIONS } from "./data";
import {
  BRAND,
  BG_CARD,
  BG_PAGE,
  BORDER,
  TEXT_SECONDARY,
  TEXT_INK,
  ICON_BTN,
  BTN_TEAL,
  BTN_OUT,
  BTN_GHOST,
  LINK,
  CHIP_OK,
  CHIP_WARN,
} from "./styles";

import AccordionRow from "./components/AccordionRow";
import NewItemMenu from "./components/NewItemMenu";
import PayTypesBody from "./components/PayTypesBody";
import SchedulesBody from "./components/SchedulesBody";
import LocationsBody from "./components/LocationsBody";

export default function PayrollItems(): React.ReactElement {
  const navigate = useNavigate();
  const loc = useLocation();

  // Initial expand state can be driven by ?expand=paytypes URL param
  const params = new URLSearchParams(loc.search);
  const initialExpand = params.get("expand");
  const [expanded, setExpanded] = useState<ExpandedState>({
    paytypes: initialExpand === "paytypes",
    deductions: initialExpand === "deductions",
    schedules: initialExpand === "schedules",
    locations: initialExpand === "locations",
  });

  function toggle(k: SectionKey): void {
    setExpanded((prev) => ({ ...prev, [k]: !prev[k] }));
  }

  function goBack(): void {
    navigate("/payroll/employees");
  }

  return (
    <div style={{ background: BG_CARD, minHeight: "100vh", width: "100%", fontFamily: "inherit" }}>
      {/* Header: back arrow + title + feedback + close */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 28px",
          borderBottom: "0.5px solid " + BORDER,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={ICON_BTN} onClick={goBack}>
            <ArrowLeft size={18} />
          </span>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: TEXT_INK,
              margin: 0,
              letterSpacing: "-0.01em",
            }}
          >
            Payroll items
          </h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            onClick={() => alert("Feedback form coming next")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 500,
              color: TEXT_SECONDARY,
              padding: "7px 11px",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            <MessageSquareText size={16} />
            How can we improve?
          </span>
          <span style={ICON_BTN} onClick={goBack}>
            <X size={18} />
          </span>
        </div>
      </div>

      {/* Intro + action buttons */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          padding: "20px 28px 24px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: 14, color: TEXT_SECONDARY, maxWidth: 460 }}>
          Create and manage payroll items for your whole team in one place.
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <NewItemMenu
            onCreatePayType={() => alert("Create pay type coming next")}
            onCreateDeduction={() => alert("Create deduction or contribution coming next")}
            onCreateSchedule={() => alert("Create pay schedule coming next")}
            onCreateLocation={() => alert("Create work location coming next")}
          />
          <button
            onClick={() => alert("Spreadsheet import coming next")}
            style={BTN_OUT}
          >
            <Sparkles size={16} style={{ color: BRAND }} />
            Try spreadsheet import
            <NovaPill style={{ marginLeft: 4 }} />
          </button>
        </div>
      </div>

      {/* The 4 accordion sections */}
      <div style={{ padding: "0 28px 24px" }}>
        <AccordionRow
          open={expanded.paytypes}
          onToggle={() => toggle("paytypes")}
          icon={<CircleDollarSign size={20} style={{ color: BRAND }} />}
          title="Pay types"
          count={PAY_TYPES.length}
          chip={
            <span style={CHIP_OK}>
              <Check size={12} />
              All set
            </span>
          }
        >
          <PayTypesBody />
        </AccordionRow>

        <AccordionRow
          open={expanded.deductions}
          onToggle={() => toggle("deductions")}
          icon={<Receipt size={20} style={{ color: BRAND }} />}
          title="Deductions & contributions"
          count={DEDUCTIONS.length}
          chip={<span style={CHIP_WARN}>None yet</span>}
        >
          <div
            style={{
              border: "1px dashed " + BORDER,
              borderRadius: 10,
              padding: 22,
              color: TEXT_SECONDARY,
              fontSize: 14,
              background: BG_PAGE,
            }}
          >
            You need to assign a deduction or contribution to at least one employee first.{" "}
            <span style={LINK} onClick={() => alert("Add deduction coming next")}>
              Add one
            </span>
          </div>
        </AccordionRow>

        <AccordionRow
          open={expanded.schedules}
          onToggle={() => toggle("schedules")}
          icon={<CalendarClock size={20} style={{ color: BRAND }} />}
          title="Pay schedules"
          count={SCHEDULES.length}
          chip={<span style={CHIP_OK}>Active</span>}
        >
          <SchedulesBody onCardClick={(s) => navigate("/payroll/items/schedules/" + s.id)} />
        </AccordionRow>

        <AccordionRow
          open={expanded.locations}
          onToggle={() => toggle("locations")}
          icon={<MapPin size={20} style={{ color: BRAND }} />}
          title="Work locations"
          count={LOCATIONS.length}
          chip={<span style={CHIP_OK}>Active</span>}
        >
          <LocationsBody onCardClick={(l) => navigate("/payroll/items/locations/" + l.id)} />
        </AccordionRow>
      </div>

      {/* Footer */}
      <div
        style={{
          background: BG_CARD,
          borderTop: "0.5px solid " + BORDER,
          padding: "16px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <button onClick={goBack} style={BTN_GHOST}>
          Cancel
        </button>
        <button onClick={goBack} style={BTN_TEAL}>
          Done
        </button>
      </div>
    </div>
  );
}